/* ─────────────────────────────────────────────────────────────────────────────
   drawerStyles.js — single source of truth for all drawer section CSS.
   Import and call injectDrawerStyles() in each drawer component.
───────────────────────────────────────────────────────────────────────────── */

export const DRAWER_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  /* ── Drawer shell ── */
  .drawer-section {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    overflow: hidden !important;
    background: #0b1120 !important;
    border-left: 1px solid #1a2540 !important;
    font-family: 'Inter', system-ui, sans-serif !important;
    min-width: 0 !important;
  }

  /* ── Section header bar ── */
  .dw-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    min-height: 48px;
    flex-shrink: 0;
    padding: 0 16px;
    border-bottom: 1px solid #1a2540;
    background: #0d1530;
  }

  /* Title inside header */
  .dw-title {
    font-size: 13.5px;
    font-weight: 700;
    color: #e8eeff;
    letter-spacing: 0.01em;
  }

  /* ── Scrollable content area ── */
  .dw-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px;
    scrollbar-width: thin;
    scrollbar-color: #1e2d4a transparent;
  }

  .dw-body::-webkit-scrollbar { width: 4px; }
  .dw-body::-webkit-scrollbar-thumb {
    background: #263554;
    border-radius: 4px;
  }

  /* ── Info row (About / JD) ── */
  .dw-row {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 4px 12px;
    margin-bottom: 16px;
    align-items: start;
  }

  .dw-label {
    font-size: 12px;
    font-weight: 500;
    color: #8b9dc3;
    padding-top: 1px;
  }

  .dw-value {
    font-size: 13px;
    font-weight: 500;
    color: #dbe4f8;
    line-height: 1.55;
    word-break: break-word;
  }

  .dw-value a {
    color: #22d3ee;
    text-decoration: none;
  }

  .dw-value a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* Sub-header (e.g. "Candidate Info") */
  .dw-subheader {
    display: flex;
    align-items: center;
    height: 40px;
    border-top: 1px solid #1a2540;
    border-bottom: 1px solid #1a2540;
    padding: 0 16px;
    margin: 8px -16px 16px;
    background: #0d1530;
  }

  .dw-subtitle {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #6b7fa8;
  }

  /* ── Notes textarea ── */
  .dw-textarea {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13.5px;
    color: #dbe4f8;
    line-height: 1.7;
    caret-color: #22d3ee;
  }

  .dw-textarea::placeholder { color: #4a5f82; }

  /* ── Chat tab navigation ── */
  .dw-chat-nav {
    display: flex;
    align-items: center;
    height: 48px;
    min-height: 48px;
    flex-shrink: 0;
    border-bottom: 1px solid #1a2540;
    background: #0d1530;
    padding: 0 4px;
    gap: 2px;
  }

  .dw-chat-tab {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 100%;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 500;
    color: #6b7fa8;
    cursor: pointer;
    border-radius: 6px 6px 0 0;
    transition: color 0.15s;
    white-space: nowrap;
  }

  .dw-chat-tab:hover { color: #c4d0ea; }

  .dw-chat-tab.dw-tab-active {
    color: #f0f4ff;
    font-weight: 600;
  }

  /* Active underline */
  .dw-chat-tab.dw-tab-active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 8px;
    right: 8px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: #22d3ee;
  }

  /* New message dot */
  .dw-new-dot {
    position: absolute;
    top: 8px;
    right: 6px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #f87171;
    border: 1.5px solid #0d1530;
  }

  /* Individual dropdown toggle */
  .dw-chat-dropdown {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    color: #6b7fa8;
    cursor: pointer;
    border-radius: 8px;
    transition: color 0.15s, background 0.15s;
    position: relative;
    white-space: nowrap;
  }

  .dw-chat-dropdown:hover { background: rgba(34,211,238,0.06); color: #c4d0ea; }
  .dw-chat-dropdown.dw-tab-active { color: #f0f4ff; font-weight: 600; }

  /* Bootstrap dropdown override — dark menu */
  .dw-dropdown-menu {
    background: #0f1629 !important;
    border: 1px solid #1e2d4a !important;
    border-radius: 10px !important;
    padding: 6px !important;
    min-width: 160px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.4) !important;
  }

  .dw-dropdown-menu .dropdown-item {
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #8b9dc3 !important;
    border-radius: 7px !important;
    padding: 7px 12px !important;
    transition: background 0.12s, color 0.12s !important;
  }

  .dw-dropdown-menu .dropdown-item:hover {
    background: rgba(34,211,238,0.09) !important;
    color: #e8eeff !important;
  }

  .dw-no-participants {
    font-size: 12px;
    color: #4a5f82;
    padding: 6px 12px;
    display: block;
  }

  /* ── Q&A ── */
  .dw-question {
    font-size: 14px;
    font-weight: 600;
    color: #e8eeff;
    line-height: 1.6;
    margin-bottom: 16px;
  }

  .dw-answers {
    padding-left: 18px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .dw-answers li {
    font-size: 13.5px;
    color: #8b9dc3;
    line-height: 1.6;
  }

  /* Q&A Pagination */
  .dw-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 24px;
    flex-wrap: wrap;
  }

  .dw-page-btn {
    all: unset;
    box-sizing: border-box;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    background: #162035;
    color: #8b9dc3;
    border: 1px solid #1e2d4a;
    transition: background 0.14s, color 0.14s, border-color 0.14s;
  }

  .dw-page-btn:hover:not(:disabled) {
    background: #1e2d4a;
    color: #f0f4ff;
  }

  .dw-page-btn.dw-page-active {
    background: rgba(34,211,238,0.14);
    color: #22d3ee;
    border-color: rgba(34,211,238,0.30);
    font-weight: 700;
  }

  .dw-page-btn:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }

  /* ── Empty state ── */
  .dw-empty {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13.5px;
    color: #4a5f82;
    padding: 32px 16px;
    text-align: center;
  }

  /* ── Copy link button ── */
  .dw-copy-btn {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    cursor: pointer;
    color: #8b9dc3;
    background: #162035;
    border: 1px solid #1e2d4a;
    font-size: 14px;
    transition: color 0.14s, background 0.14s;
    flex-shrink: 0;
  }

  .dw-copy-btn:hover { background: #1e2d4a; color: #22d3ee; }
  .dw-copy-btn.dw-copied { color: #4ade80; border-color: rgba(74,222,128,0.3); }

  /* ── Mobile modal dark override ── */
  .dw-mobile-modal .modal-content {
    background: #0f1629 !important;
    border: 1px solid #1e2d4a !important;
    border-radius: 16px !important;
    font-family: 'Inter', system-ui, sans-serif !important;
  }

  .dw-mobile-modal .modal-body { padding: 0 !important; height: 550px !important; overflow: hidden !important; }
  .dw-mobile-modal .modal-footer {
    background: #0b1120 !important;
    border-top: 1px solid #1a2540 !important;
    padding: 12px 16px !important;
  }

  .dw-close-btn {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    padding: 8px 18px;
    background: #162035;
    color: #8b9dc3;
    border: 1px solid #1e2d4a;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.14s, color 0.14s;
  }

  .dw-close-btn:hover { background: #1e2d4a; color: #e8eeff; }

  /* JD html content theming */
  .dw-jd-content { color: #c4d0ea; font-size: 13.5px; line-height: 1.7; }
  .dw-jd-content h1, .dw-jd-content h2, .dw-jd-content h3 { color: #f0f4ff; margin-top: 16px; }
  .dw-jd-content ul, .dw-jd-content ol { padding-left: 20px; color: #8b9dc3; }
  .dw-jd-content strong, .dw-jd-content b { color: #dbe4f8; }
  .dw-jd-content a { color: #22d3ee; }
`;

let _injected = false;
export const injectDrawerStyles = () => {
  if (_injected) return;
  const s = document.createElement("style");
  s.textContent = DRAWER_STYLES;
  document.head.appendChild(s);
  _injected = true;
};
